import React from "react";
import { Link } from "react-router-dom";
import { useSettings } from "../contexts/SettingsContext";
import api, { MEDIA } from "../lib/api";
import { toast } from "sonner";

const DEFAULT_BRAND = "ccashop";

export default function Footer() {
    const { settings } = useSettings();
    const [email, setEmail] = React.useState("");
    const [cats, setCats] = React.useState([]);

    React.useEffect(() => {
        api.get("/categories").then(r => setCats(r.data || [])).catch(() => {});
    }, []);

    const subscribe = async (e) => {
        e.preventDefault();
        try {
            await api.post("/newsletter/subscribe", { email });
            toast.success("Te-ai abonat cu succes la newsletter!");
            setEmail("");
        } catch {
            toast.error("Eroare. Te rugăm să încerci din nou.");
        }
    };

    const brandName = settings.site_name || settings.company_name || DEFAULT_BRAND;
    // Only top-level categories in footer
    const topCats = cats.filter(c => !c.parent_id).slice(0, 8);

    const resolveVars = (text) => {
        if (!text) return "";
        const vars = {
            company_name:    settings.company_name || brandName,
            site_name:       settings.site_name    || brandName,
            site_url:        settings.site_base_url || window.location.origin,
            company_cui:     settings.company_cui     || "",
            company_reg_no:  settings.company_reg_no  || "",
            company_address: settings.company_address || "",
            contact_email:   settings.contact_email   || "",
            contact_phone:   settings.contact_phone   || "",
            current_year:    new Date().getFullYear(),
        };
        return text.replace(/\{(\w+)\}/g, (_, k) => vars[k] ?? `{${k}}`);
    };

    return (
        <footer className="mt-24 bg-foreground text-background">
            <div className="container-x py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
                {/* Brand & Contact */}
                <div className="md:col-span-4">
                    <div className="flex items-center gap-2 mb-4">
                        {settings.logo_url ? (
                            <img src={settings.logo_url.startsWith("http") ? settings.logo_url : MEDIA(settings.logo_url)}
                                 alt={brandName}
                                 className="h-10 max-w-[180px] object-contain brightness-0 invert" />
                        ) : (
                            <div className="font-heading font-bold text-2xl">{brandName}</div>
                        )}
                    </div>
                    <p className="text-sm text-background/70 leading-relaxed mb-4">
                        {settings.company_tagline || "Sisteme profesionale de securitate și automatizări cu instalare inclusă."}
                    </p>
                    <div className="font-mono text-xs text-background/60 space-y-1">
                        {settings.contact_phone && <div>☏ {settings.contact_phone}</div>}
                        {settings.contact_email && <div>✉ {settings.contact_email}</div>}
                        {settings.contact_address && <div>⌖ {settings.contact_address}</div>}
                        {settings.company_cui && <div>CUI: {settings.company_cui}</div>}
                    </div>
                    {/* Social links */}
                    {(settings.social_facebook || settings.social_youtube || settings.social_tiktok || settings.social_instagram) && (
                        <div className="flex gap-3 mt-4">
                            {settings.social_facebook && (
                                <a href={settings.social_facebook} target="_blank" rel="noopener noreferrer" className="text-background/50 hover:text-background transition-colors" aria-label="Facebook">
                                    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                                </a>
                            )}
                            {settings.social_youtube && (
                                <a href={settings.social_youtube} target="_blank" rel="noopener noreferrer" className="text-background/50 hover:text-background transition-colors" aria-label="YouTube">
                                    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                                </a>
                            )}
                            {settings.social_tiktok && (
                                <a href={settings.social_tiktok} target="_blank" rel="noopener noreferrer" className="text-background/50 hover:text-background transition-colors" aria-label="TikTok">
                                    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.75a4.85 4.85 0 0 1-1.01-.06z"/></svg>
                                </a>
                            )}
                            {settings.social_instagram && (
                                <a href={settings.social_instagram} target="_blank" rel="noopener noreferrer" className="text-background/50 hover:text-background transition-colors" aria-label="Instagram">
                                    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"/></svg>
                                </a>
                            )}
                        </div>
                    )}
                </div>

                {/* Magazin — only top-level categories */}
                {topCats.length > 0 && (
                    <div className="md:col-span-2">
                        <h4 className="font-heading text-sm uppercase tracking-widest mb-4 text-primary">Magazin</h4>
                        <ul className="space-y-2 text-sm">
                            {topCats.map(c => (
                                <li key={c.category_id}>
                                    <Link to={`/magazin?category=${c.slug}`} className="text-background/70 hover:text-background transition-colors" data-testid={`footer-cat-${c.slug}`}>
                                        {c.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Companie */}
                <div className="md:col-span-2">
                    <h4 className="font-heading text-sm uppercase tracking-widest mb-4 text-primary">Companie</h4>
                    <ul className="space-y-2 text-sm">
                        <li><Link to="/despre" className="text-background/70 hover:text-background transition-colors">Despre noi</Link></li>
                        <li><Link to="/servicii" className="text-background/70 hover:text-background transition-colors">Servicii</Link></li>
                        <li><Link to="/portofoliu" className="text-background/70 hover:text-background transition-colors">Portofoliu</Link></li>
                        <li><Link to="/blog" className="text-background/70 hover:text-background transition-colors">Blog</Link></li>
                        <li><Link to="/contact" className="text-background/70 hover:text-background transition-colors">Contact</Link></li>
                    </ul>
                </div>

                {/* Newsletter + Legal */}
                <div className="md:col-span-4">
                    <h4 className="font-heading text-sm uppercase tracking-widest mb-4 text-primary">Newsletter</h4>
                    <p className="text-sm text-background/70 mb-4">Primești primul ofertele și ghidurile noastre.</p>
                    <form onSubmit={subscribe} className="flex items-stretch border-2 border-background mb-6">
                        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="emailul tău"
                               className="px-4 py-3 bg-transparent outline-none flex-1 text-sm text-background placeholder:text-background/50" data-testid="footer-newsletter-input" />
                        <button type="submit" className="px-6 bg-primary text-primary-foreground font-mono uppercase text-xs" data-testid="footer-newsletter-btn">Abonare</button>
                    </form>
                    <div className="space-y-2 text-sm">
                        <Link to="/termeni" className="block text-background/70 hover:text-background transition-colors" data-testid="footer-terms">Termeni și condiții</Link>
                        <Link to="/gdpr" className="block text-background/70 hover:text-background transition-colors" data-testid="footer-gdpr">Politica GDPR</Link>
                        <Link to="/retur" className="block text-background/70 hover:text-background transition-colors" data-testid="footer-return">Politică de retur</Link>
                    </div>
                </div>
            </div>

            {/* ANPC — SAL / SOL — obligatoriu conform OG 38/2015 & Reg. UE 524/2013 */}
            <div className="border-t border-background/20 bg-background/5">
                <div className="container-x py-5 flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-3 flex-wrap justify-center md:justify-start">
                        <a href="https://anpc.ro/ce-este-sal/" target="_blank" rel="noopener noreferrer"
                           className="flex items-center hover:opacity-80 transition-opacity"
                           data-testid="footer-anpc-sal"
                           title="Soluționarea Alternativă a Litigiilor — ANPC">
                            <ANPCSALBadge />
                        </a>
                        <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer"
                           className="flex items-center hover:opacity-80 transition-opacity"
                           data-testid="footer-anpc-sol"
                           title="Soluționarea Online a Litigiilor — platformă UE">
                            <SOLODRBadge />
                        </a>
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-background/40 text-center md:text-right">
                        © {new Date().getFullYear()} {brandName}. Toate drepturile rezervate.
                    </div>
                </div>
            </div>

            {/* Legal text from settings */}
            {settings.footer_legal_text && (
                <div className="border-t border-background/10">
                    <div className="container-x py-5">
                        <p className="text-[11px] text-background/40 leading-relaxed whitespace-pre-line">
                            {resolveVars(settings.footer_legal_text)}
                        </p>
                    </div>
                </div>
            )}
        </footer>
    );
}

// Official-style ANPC SAL badge
function ANPCSALBadge() {
    return (
        <svg viewBox="0 0 120 44" className="h-10 w-auto" aria-label="ANPC — Soluționarea Alternativă a Litigiilor">
            <rect width="120" height="44" rx="5" fill="#003399" />
            <rect x="2" y="2" width="116" height="40" rx="4" fill="none" stroke="#ffcc00" strokeWidth="1.5" />
            {/* EU stars */}
            {Array.from({ length: 12 }).map((_, i) => {
                const a = (i * 30 - 90) * Math.PI / 180;
                const cx = 22 + 9 * Math.cos(a);
                const cy = 22 + 9 * Math.sin(a);
                return (
                    <polygon key={i} fill="#ffcc00"
                        points={`${cx},${cy - 2} ${cx + 0.8},${cy - 0.6} ${cx + 2},${cy - 0.6} ${cx + 1.2},${cy + 0.4} ${cx + 1.4},${cy + 1.8} ${cx},${cy + 1} ${cx - 1.4},${cy + 1.8} ${cx - 1.2},${cy + 0.4} ${cx - 2},${cy - 0.6} ${cx - 0.8},${cy - 0.6}`}
                    />
                );
            })}
            <text x="40" y="17" fontSize="8.5" fontWeight="900" fill="#ffffff" fontFamily="Arial,sans-serif" letterSpacing="0.5">ANPC</text>
            <text x="40" y="28" fontSize="7.5" fontWeight="700" fill="#ffcc00" fontFamily="Arial,sans-serif" letterSpacing="0.5">SAL</text>
            <text x="40" y="37" fontSize="5.5" fill="#ffffff" fontFamily="Arial,sans-serif" opacity="0.7">Alt. Litigii</text>
        </svg>
    );
}

// Official-style EU SOL/ODR badge
function SOLODRBadge() {
    return (
        <svg viewBox="0 0 140 44" className="h-10 w-auto" aria-label="UE — Soluționarea Online a Litigiilor">
            <rect width="140" height="44" rx="5" fill="#003399" />
            <rect x="2" y="2" width="136" height="40" rx="4" fill="none" stroke="#ffcc00" strokeWidth="1.5" />
            {/* EU stars */}
            {Array.from({ length: 12 }).map((_, i) => {
                const a = (i * 30 - 90) * Math.PI / 180;
                const cx = 22 + 9 * Math.cos(a);
                const cy = 22 + 9 * Math.sin(a);
                return (
                    <polygon key={i} fill="#ffcc00"
                        points={`${cx},${cy - 2} ${cx + 0.8},${cy - 0.6} ${cx + 2},${cy - 0.6} ${cx + 1.2},${cy + 0.4} ${cx + 1.4},${cy + 1.8} ${cx},${cy + 1} ${cx - 1.4},${cy + 1.8} ${cx - 1.2},${cy + 0.4} ${cx - 2},${cy - 0.6} ${cx - 0.8},${cy - 0.6}`}
                    />
                );
            })}
            <text x="40" y="16" fontSize="7.5" fontWeight="900" fill="#ffffff" fontFamily="Arial,sans-serif" letterSpacing="0.5">UNIUNEA EUROPEANĂ</text>
            <text x="40" y="27" fontSize="8" fontWeight="700" fill="#ffcc00" fontFamily="Arial,sans-serif" letterSpacing="0.5">SOL / ODR</text>
            <text x="40" y="37" fontSize="5.5" fill="#ffffff" fontFamily="Arial,sans-serif" opacity="0.7">Litigii online UE</text>
        </svg>
    );
}
